import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { ChevronsRight, Plus, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import type { WatcherSnapshot, WatchViewSnapshot } from '@/lib/log-watch/types';
import { keyToWatcherId } from '@/lib/log-watch/useDeviceKeyWatchers';
import { KeyRow } from './KeyRow';

/**
 * 日志摘要面板（M2 简化版）。
 *
 * 功能：
 *   - 顶栏：标题 + 全清按钮（仅 keys 非空时出现）+ 收起按钮（onClose）；
 *   - 输入区：单行 input + `+` 按钮，回车 / 点 `+` 都可加 key；
 *   - 列表：每个 key 一张 `KeyRow`，命中后展示最新值 / 历史 / 跳行；
 *   - 空态：突出说明使用方式（"先添加 oaid 这种 key，引擎会按 oaid=val /
 *     oaid: val 抽出后面的值"）。
 *
 * 不再有 view / yaml / mounted / refresh / pause 等概念。
 */

interface Props {
  keys: string[];
  /** 来自 useDeviceKeyWatchers 的单 view snapshot，keys 为空时 undefined。 */
  snapshot?: WatchViewSnapshot;
  onAddKey: (key: string) => void;
  onRemoveKey: (key: string) => void;
  onClearKeys: () => void;
  onClose?: () => void;
  onJumpToLine?: (lineId: number) => void;
  /** 当前 RunnerSet 已分配到的最大 lineId（与 checkpoint 共享行号空间）。 */
  nextLineId: number;
}

export function WatchPanel({
  keys,
  snapshot,
  onAddKey,
  onRemoveKey,
  onClearKeys,
  onClose,
  onJumpToLine,
  nextLineId,
}: Props): JSX.Element {
  const [draft, setDraft] = useState('');

  // watcherId -> snapshot；用于按 keys 顺序反查每行展示的 snapshot。
  // 引擎里 watcher.id 是从 keyToWatcherId(key) 计算得来的，UI 层用同一函数
  // 反查；keys 没启动 runner（serial undefined）时 snapshot 整个为空，
  // KeyRow 自己会处理"无命中"态。
  const snapshotByWatcherId = useMemo(() => {
    const map = new Map<string, WatcherSnapshot>();
    if (snapshot) for (const w of snapshot.watchers) map.set(w.watcherId, w);
    return map;
  }, [snapshot]);

  const totalHits = useMemo(
    () => snapshot?.watchers.reduce((sum, w) => sum + w.count, 0) ?? 0,
    [snapshot],
  );

  const submit = useCallback(
    (e?: FormEvent): void => {
      e?.preventDefault();
      const trimmed = draft.trim();
      if (!trimmed) return;
      onAddKey(trimmed);
      setDraft('');
    },
    [draft, onAddKey],
  );

  return (
    <aside className="flex h-full w-full min-w-0 flex-col gap-2 rounded-md border border-line bg-bg-soft p-2">
      <header className="flex items-center justify-between">
        <h2 className="text-xs font-semibold tracking-tight text-ink">
          取值规则
          <span className="ml-1.5 font-mono text-[10px] text-ink-dim">
            {keys.length} key · {totalHits} 命中
          </span>
        </h2>
        <div className="inline-flex items-center gap-1">
          {keys.length > 0 && (
            <button
              type="button"
              className="rounded-md border border-line bg-bg-card px-1.5 py-1 text-ink-muted hover:bg-bg-soft hover:text-rose-300"
              title="清空所有 key"
              onClick={onClearKeys}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="rounded-md border border-line bg-bg-card px-1.5 py-1 text-ink-muted hover:bg-bg-soft"
              title="收起规则面板"
              onClick={onClose}
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </header>
      <form className="flex items-center gap-1" onSubmit={submit}>
        <input
          autoFocus
          className="input flex-1 font-mono text-[11px]"
          placeholder="输入键名（如 oaid），回车添加"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button
          type="submit"
          className={clsx(
            'inline-flex items-center gap-1 rounded-md border border-line bg-accent px-2 py-1 text-[11px] font-medium text-white shadow-sm',
            !draft.trim() && 'opacity-50',
          )}
          title="添加 key"
          disabled={!draft.trim()}
        >
          <Plus className="h-3 w-3" /> 添加
        </button>
      </form>
      <div className="flex-1 overflow-y-auto pr-0.5">
        {keys.length === 0 ? (
          <div className="rounded-md border border-dashed border-line p-3 text-[11px] leading-relaxed text-ink-muted">
            <p className="mb-1 text-ink">在上方输入框敲一个键名（如 <code className="font-mono">oaid</code>、<code className="font-mono">gaid</code>、<code className="font-mono">ecpm</code>）按回车，引擎会扫描当前 logcat：</p>
            <ul className="ml-3 list-disc space-y-0.5 text-ink-muted">
              <li>遇到 <code className="font-mono">oaid=ABCDEF</code> / <code className="font-mono">oaid: ABCDEF</code> 自动抽出 <code className="font-mono">ABCDEF</code>；</li>
              <li>值的边界按空格 / 逗号 / 分号断开，避免吞掉整个尾巴；</li>
              <li>每条命中点击可以跳回原日志行。</li>
            </ul>
            <p className="mt-1 text-ink-dim">key 列表按设备保存，同设备多 tab 实时同步。</p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {keys.map((k) => {
              const id = keyToWatcherId(k);
              return (
                <KeyRow
                  key={k}
                  keyName={k}
                  snapshot={snapshotByWatcherId.get(id)}
                  onRemove={onRemoveKey}
                  onJumpToLine={onJumpToLine}
                />
              );
            })}
          </ul>
        )}
      </div>
      <footer className="flex items-center gap-1 border-t border-line pt-1 text-[10px] text-ink-muted">
        <span>{keys.length} key</span>
        <span className="ml-auto font-mono">行 {nextLineId.toLocaleString()}</span>
      </footer>
    </aside>
  );
}
